import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  ForemanDateToProcess,
  ForemanEmployeeDispatch,
  ForemanEmployeeImportBatch,
  ForemanEmployeeImportWeekOption,
  ForemanEmployeeListItem,
  ForemanJobImport,
  ForemanTimeImport,
  TimekeepingDto,
} from '@ups/xplat/api/dto';
import {
  environment,
  MyHttpClientFactory,
  ResponseCasingEnum,
} from '@ups/xplat/core';
import { deepClone, formatDateAsIsoLocal } from '@ups/xplat/utils';
import { Observable } from 'rxjs';
import { normalizeTimekeepingDtoCalendarDates } from './timekeeping-service';

@Injectable({
  providedIn: 'root',
})
export class ForemanTimeImportService {
  httpFactory = inject(MyHttpClientFactory);
  http: HttpClient;

  constructor() {
    const url = environment.urls.spartaAPI;
    this.http = this.httpFactory.createHttpClient(
      url,
      true,
      ResponseCasingEnum.CamelCase
    );
  }

  getForemanJobList(includeCompleted: boolean): Observable<ForemanJobImport[]> {
    const url = `/api/foreman/importTime/jobs?includeCompleted=${includeCompleted}`;
    return this.http.get(url) as Observable<ForemanJobImport[]>;
  }

  getForemanEmployeeList(
    includeCompleted = false
  ): Observable<ForemanEmployeeListItem[]> {
    const url = `/api/foreman/importTime/employees?includeCompleted=${includeCompleted}`;
    return this.http.get(url) as Observable<ForemanEmployeeListItem[]>;
  }

  getForemanEmployeeImportWeeks(
    employeeID: string,
    includeCompleted = false
  ): Observable<ForemanEmployeeImportWeekOption[]> {
    const url = `/api/foreman/importTime/employees/${employeeID}/weeks?includeCompleted=${includeCompleted}`;
    return this.http.get(url) as Observable<ForemanEmployeeImportWeekOption[]>;
  }

  getTimekeepingRecordsToImportByEmployee(
    employeeID: string,
    weekStart: Date,
    includeCompleted: boolean,
    foremanCrew?: string,
    workOrder?: string,
    shift?: number
  ): Observable<ForemanEmployeeImportBatch[]> {
    const url = `api/foreman/importTime/employees/${employeeID}/${formatDateAsIsoLocal(
      weekStart
    )}`;

    const queryParameters: string[] = [];

    if (includeCompleted) {
      queryParameters.push(`includeCompleted=${includeCompleted}`);
    }
    if (foremanCrew) {
      queryParameters.push(`foremanCrews=${foremanCrew}`);
    }
    if (workOrder) {
      queryParameters.push(`workOrders=${workOrder}`);
    }
    if (shift) {
      queryParameters.push(`shifts=${shift}`);
    }

    const query = queryParameters.length ? `?${queryParameters.join('&')}` : '';
    return this.http.get(url + query) as Observable<
      ForemanEmployeeImportBatch[]
    >;
  }

  getForemanDateList(
    foremanJobID: string,
    includeCompleted: boolean
  ): Observable<ForemanDateToProcess[]> {
    const url = `/api/foreman/importTime/${foremanJobID}/dates?includeCompleted=${includeCompleted}`;
    return this.http.get(url) as Observable<ForemanDateToProcess[]>;
  }

  getEmployeeTimeEntriesToImport(
    foremanJobID: string,
    date: Date
  ): Observable<ForemanEmployeeDispatch[]> {
    const url = `api/foreman/importTime/employee/${foremanJobID}/${formatDateAsIsoLocal(
      date
    )}`;
    return this.http.get(url) as Observable<ForemanEmployeeDispatch[]>;
  }

  getTimekeepingRecordsToImport(
    foremanJobID: string,
    date: Date,
    includeCompleted: boolean,
    foremanCrew?: string,
    workOrder?: string,
    shift?: number
  ): Observable<ForemanTimeImport[]> {
    const url = `api/foreman/importTime/${foremanJobID}/${formatDateAsIsoLocal(
      date
    )}`;

    const queryParameters: string[] = [];

    if (includeCompleted) {
      queryParameters.push(`includeCompleted=${includeCompleted}`);
    }
    if (foremanCrew) {
      queryParameters.push(`foremanCrews=${foremanCrew}`);
    }
    if (workOrder) {
      queryParameters.push(`workOrders=${workOrder}`);
    }
    if (shift) {
      queryParameters.push(`shifts=${shift}`);
    }

    return this.http.get(url + '?' + queryParameters.join('&')) as Observable<
      ForemanTimeImport[]
    >;
  }

  importTimekeepingRecords(
    foremanJobID: string,
    date: Date,
    dtos: TimekeepingDto[]
  ): Observable<ForemanTimeImport[]> {
    const url = `api/foreman/import/${foremanJobID}/${formatDateAsIsoLocal(
      date
    )}`;

    const body = dtos.map((d) => {
      const copy = deepClone(d);
      normalizeTimekeepingDtoCalendarDates(copy);
      return copy;
    });
    return this.http.post(url, body) as Observable<ForemanTimeImport[]>;
  }

  mapForemanJobToSystemJob(foremanJobID: string, jobID: string) {
    const url = `/api/foreman/import/map/job`;

    const dto = [
      {
        foremanJobID,
        jobID,
      },
    ];

    return this.http.post(url, dto);
  }

  mapForemanPhaseToSystemPhase(foremanPhaseID: string, phaseID: string) {
    const url = `/api/foreman/import/map/phase`;

    const dto = [
      {
        foremanPhaseID,
        phaseID,
      },
    ];

    return this.http.post(url, dto);
  }
}
